import { Theme } from 'themes/interfaces';
import { createTheme } from '@mui/material/styles';
import { EnumTheme } from './enums';

export const defaultTheme = (theme: Theme) =>
  createTheme({
    direction: "ltr",
    breakpoints: {
      values: {
        xs: 0,
        sm: 640,
        md: 1024,
        lg: 1200,
        xl: 1440,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            height: "100%",
            scrollBehavior: "smooth",
          },
          body: {
            margin: 0,
            minHeight: "100%",
            position: "relative",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            backgroundColor: theme.color.background.color,
            color: theme.color.font.color,
            fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            transition: "background-color 0.35s ease-in-out, color 0.35s ease-in-out",
          },
          "#root": {
            minHeight: "100%",
            display: "flex",
            flexDirection: "column",
          },
          "code, kbd, samp, pre": {
            fontFamily: '"Space Mono", monospace !important',
          },
          "*, *::before, *::after": {
            boxSizing: "border-box",
            transition: "border-color 0.35s ease-in-out, background-color 0.35s ease-in-out, box-shadow 0.35s ease-in-out",
          },
        },
      },
    },
    palette: {
      mode: theme.id === EnumTheme.Dark ? "dark" : "light",
      common: {
        black: theme.color.black,
        white: theme.color.white,
      },
      primary: {
        main: theme.color.primary.color,
        contrastText: theme.color.primary.text,
      },
      secondary: {
        main: theme.color.secondary.color,
        contrastText: theme.color.secondary.text,
      },
      error: {
        main: theme.color.status.error.color,
        contrastText: theme.color.status.error.text,
      },
      warning: {
        main: theme.color.status.warning.color,
        contrastText: theme.color.status.warning.text,
      },
      success: {
        main: theme.color.status.success.color,
        contrastText: theme.color.status.success.text,
      },
      text: {
        primary: theme.color.font.color,
        secondary: theme.color.font.accent,
        disabled: theme.color.disabled.text,
      },
      divider: theme.color.border.color,
      background: {
        default: theme.color.background.color,
        paper: theme.color.background.accent,
      },
    },
    typography: {
      htmlFontSize: 16,
      fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: 14,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: {
        fontFamily: '"Plus Jakarta Sans", sans-serif',
        fontWeight: 800,
        fontSize: "3.5rem",
        lineHeight: 1.15,
        letterSpacing: "-0.03em",
      },
      h2: {
        fontFamily: '"Plus Jakarta Sans", sans-serif',
        fontWeight: 700,
        fontSize: "2.25rem",
        lineHeight: 1.25,
        letterSpacing: "-0.02em",
      },
      h3: {
        fontFamily: '"Plus Jakarta Sans", sans-serif',
        fontWeight: 600,
        fontSize: "1.5rem",
        lineHeight: 1.35,
        letterSpacing: "-0.01em",
      },
      h4: {
        fontFamily: '"Plus Jakarta Sans", sans-serif',
        fontWeight: 600,
        fontSize: "1.125rem",
        lineHeight: 1.45,
        letterSpacing: "0em",
      },
      h5: {
        fontFamily: '"Plus Jakarta Sans", sans-serif',
        fontWeight: 500,
        fontSize: "1rem",
        lineHeight: 1.5,
        letterSpacing: "0em",
      },
      h6: {
        fontFamily: '"Plus Jakarta Sans", sans-serif',
        fontWeight: 500,
        fontSize: "0.875rem",
        lineHeight: 1.55,
        letterSpacing: "0.01em",
      },
      subtitle1: {
        fontFamily: '"Plus Jakarta Sans", sans-serif',
        fontWeight: 400,
        fontSize: "1.125rem",
        lineHeight: 1.65,
        letterSpacing: "-0.005em",
      },
      subtitle2: {
        fontFamily: '"Space Mono", monospace',
        fontWeight: 700,
        fontSize: "0.6875rem",
        lineHeight: 1.4,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
      },
      body1: {
        fontFamily: '"Plus Jakarta Sans", sans-serif',
        fontWeight: 400,
        fontSize: "0.9375rem",
        lineHeight: 1.6,
        letterSpacing: "0em",
      },
      body2: {
        fontFamily: '"Plus Jakarta Sans", sans-serif',
        fontWeight: 400,
        fontSize: "0.8125rem",
        lineHeight: 1.5,
        letterSpacing: "0.01em",
      },
      button: {
        fontFamily: '"Plus Jakarta Sans", sans-serif',
        fontWeight: 600,
        fontSize: "0.875rem",
        lineHeight: 1.5,
        letterSpacing: "0.02em",
        textTransform: "none",
      },
      caption: {
        fontFamily: '"Space Mono", monospace',
        fontWeight: 700,
        fontSize: "0.625rem",
        lineHeight: 1.3,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
      },
      overline: {
        fontFamily: '"Space Mono", monospace',
        fontWeight: 700,
        fontSize: "0.6875rem",
        lineHeight: 1.5,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
      },
    },
  });
